import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { IconRefresh, IconEye } from '@tabler/icons-react';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import { CardsLegendContainer, CardsLegendItemContainer, InstanceDetailsContainer } from '../../Styles/InstanceManagement/instanceManagementStyles';

import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { InstanceManagementListField } from '../../Utilities/Enums/ParametersEnum';
import {
    DBBackupStatusEnum,
    DBBackupStatusTextEnum,
    InstanceStatusEnum,
    InstanceStatusTextEnum,
    SchemaHealthStatusEnum,
    SchemaHealthStatusTextEnum,
} from '../../Utilities/Enums/StatusEnums';

import CustomListHeader from '../../CustomViews/Pages/ListComponent/CustomListHeader';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomListSearch from '../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';

import { InstanceMngtList } from '../../Models/InstanceManagement/InstanceManagement';

import { useInstanceManagementHook } from '../../Hooks/InstanceManagement/instanceManagementHooks';

export default function InstanceManagementList() {
    let isDarkMode = LocalStorageHelper.isDarkMode();
    const navigate = useNavigate();

    // states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        sortBy: QueryBuilderHelper.DefaultSortValue(InstanceManagementListField.CREATED_DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });

    const q_queryKey = [CachingEnum.INSTANCE_MANAGEMENT_LIST, queryStr]
    const { data, fetchStatus, instanceRefreshAll } = useInstanceManagementHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: InstanceManagementListField.DATABASE,
            title: 'Instance',
            sortable: true,
            render: (record: InstanceMngtList) => <InstanceDetailsContainer $darkMode={isDarkMode}>
                <span className='instanceName' 
                onClick={() => {
                    navigate(`${RouteEnum.INSTANCE_MANAGEMENT_DETAILS}?id=${record?.[InstanceManagementListField.PUBLIC_ID] }`)
                }}>
                    {record?.[InstanceManagementListField.DATABASE] ?? "--"}
                </span>
                <span className='engineName'>{record?.[InstanceManagementListField.IS_ON_PREMISE] ? 'On-premise' : 'Cloud'} . {record?.[InstanceManagementListField.ENGINE] ?? "--"}</span>
            </InstanceDetailsContainer>
        },
        {
            accessor: InstanceManagementListField.COMPANY_NAME,
            title: 'Company',
            sortable: true,
            render: (record: InstanceMngtList) => <InstanceDetailsContainer $darkMode={isDarkMode}>
                <span className='companyName'>{record?.[InstanceManagementListField.COMPANY_NAME] ?? "--"}</span>
            </InstanceDetailsContainer>
        },
        {
            accessor: InstanceManagementListField.STATUS,
            title: 'Status',
            sortable: true,
            render: (record: InstanceMngtList) => {
                let status: InstanceStatusEnum = record?.[InstanceManagementListField.STATUS] <= 0 ? InstanceStatusEnum.OFFLINE : record?.[InstanceManagementListField.STATUS];
                let statusText = InstanceStatusTextEnum[status];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className={'connectionStatus ' + statusText.toLowerCase()}>{statusText}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceManagementListField.DB_VERSION,
            title: 'DB Version',
            sortable: true,
            render: (record: InstanceMngtList) => {
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className="dbVersion">{record?.[InstanceManagementListField.DB_VERSION] ?? "--"}</span>
                    {(record?.[InstanceManagementListField.PENDING_MIGRATIONS] ?? 0) > 0 && <span className="pendingMigrations">{record?.[InstanceManagementListField.PENDING_MIGRATIONS] ?? 0} pending migrations</span>}
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceManagementListField.SCHEMA_HEALTH,
            title: 'Schema',
            sortable: true,
            render: (record: InstanceMngtList) => {
                let schemaHealth: SchemaHealthStatusEnum = record?.[InstanceManagementListField.SCHEMA_HEALTH] ?? SchemaHealthStatusEnum.HEALTHY;
                let schemaHealthText = SchemaHealthStatusTextEnum[schemaHealth];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className={'schemaHealth ' + schemaHealthText.toLowerCase()}><span className='dotText'>&nbsp;</span>{schemaHealthText}</span>
                </InstanceDetailsContainer>
            }
        },
        // {
        //     accessor: InstanceManagementListField.SP_INTEGRITY,
        //     title: 'SP Integrity',
        //     sortable: true,
        //     render: (record: InstanceMngtList) => {
        //         let schemaHealth: SchemaHealthStatusEnum = record?.[InstanceManagementListField.SCHEMA_HEALTH] ?? SchemaHealthStatusEnum.HEALTHY;
        //         let schemaHealthText = SchemaHealthStatusTextEnum[schemaHealth];
        //         let spTotal = record?.[InstanceManagementListField.STORED_PROC_TOTAL] ?? 0;
        //         let spValid = record?.[InstanceManagementListField.STORED_PROC_VALID] ?? 0;
        //         let outdatedCount = 0;
                
        //         return <InstanceDetailsContainer $darkMode={isDarkMode}>
        //             <span className={'spIntegrity ' + schemaHealthText.toLowerCase()}>{spValid} / {spTotal}</span>
        //             <span className='validCounts'>{outdatedCount > 0 ? outdatedCount + " outdated" : (spValid == spTotal ? "all valid" : (spTotal - spValid) + " invalid")}</span>
        //         </InstanceDetailsContainer>
        //     }
        // },
        {
            accessor: InstanceManagementListField.LAST_BACKUP_DATE,
            title: 'Last Backup',
            sortable: true,
            render: (record: InstanceMngtList) => {
                let dbBackupStatus: DBBackupStatusEnum = record?.[InstanceManagementListField.BACKUP_STATUS] ?? DBBackupStatusEnum.ENABLED;
                let dbBackupStatusText = DBBackupStatusTextEnum[dbBackupStatus];
                let lastBackupDate: string | Date | null = record?.[InstanceManagementListField.LAST_BACKUP_DATE] != null && record?.[InstanceManagementListField.LAST_BACKUP_DATE] != "" ? DateHelper.getFormattedDate(record?.[InstanceManagementListField.LAST_BACKUP_DATE], "dd/MM/yyyy hh:mm a") : null;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    {dbBackupStatus == DBBackupStatusEnum.ENABLED && <span className='dateText'>{lastBackupDate ?? '--'}</span> }
                    {dbBackupStatus != DBBackupStatusEnum.ENABLED && <span className={'dbBackupStatus ' + dbBackupStatusText.toLowerCase()}>{dbBackupStatusText}</span> }
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: '#',
            title: "Action",
            render: (record: InstanceMngtList) => <>
                <CustomIconButton toolTipName='Manage Instance'
                    icon={<IconEye />}
                    onClick={() => {
                        navigate(`${RouteEnum.INSTANCE_MANAGEMENT_DETAILS}?id=${record?.[InstanceManagementListField.PUBLIC_ID] }`)
                    }} />
            </>
        },
    ];

    const renderCardsLegend = () => {
        return <CardsLegendContainer>
            {data?.cardsLegend?.map((cardLegend: any) => (
                <CardsLegendItemContainer elevation={2} $darkMode={isDarkMode}>
                    <div className='itemDetailsContainer'>
                        <span className='itemCount'>{cardLegend?.count ?? "--"}</span>
                        <span className='itemTitle'>{cardLegend?.title ?? "--"}</span>
                    </div>
                    <div className={'itemIconContainer ' + cardLegend.color}>{cardLegend?.icon ?? null}</div>
                    <span className='subNote'>{cardLegend?.subNotes ?? "--"}</span>
                </CardsLegendItemContainer>
            ))}
        </CardsLegendContainer>;
    }

    const renderTable = () => {
        return <TableContainer elevation={2} $hadBoxShadow={true}>
            <CustomListHeader title={RouteHelper.getRouteDescription(RouteEnum.INSTANCE_MANAGEMENT, true)}
                subTitle=""
                headerButtons={<>
                    <CustomButton
                        btnType={ButtonType.PRIMARY_LIGHT}
                        title='Refresh All'
                        startIcon={<IconRefresh />}
                        onClick={() => {
                            if(instanceRefreshAll) {
                                instanceRefreshAll();
                            }
                        }} />
                </>} />
            <CustomListSearch searchValue={queryStr.searchString ?? ""}
                hideFilterBtn
                onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
                searchPlaceholder='Search Name / Username'
                legends={data?.legends}
                onClickLegendItem={(id) => setQueryStr(prev => ({ ...prev, status: id, pageNumber: 1 }))}>
            </CustomListSearch>

            <CustomListComponent
                fetching={fetchStatus == "fetching"}
                columns={tableColumns}
                idAccessor={(record: InstanceMngtList) => `Instance${record?.[InstanceManagementListField.PUBLIC_ID]}`}
                records={data?.data?.list ?? []}
                totalRecords={data?.data?.metaData?.totalCount ?? 0}
                page={queryStr.pageNumber}
                recordsPerPage={queryStr.pageSize}
                defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
                onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
                setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
                onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />
        </TableContainer>
    }

    return <>
        {renderCardsLegend()}
        {renderTable()}
    </>
}
